import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { closeCart } from "../../store/cartSlice";
import "./CartOverlay.scss";
import { useLocation } from "react-router";
import Button from "../ui/Button";
import ModalWrapper from "../ui/ModalWrapper";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { calculateSubtotal, calculateTotal, formatDisplayPrice } from "../../utils/priceHandler";
import CartItem from "./CartItem";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import ProductPrice from "../product/ProductPrice";
import { trackCTA } from "../../utils/trackCTA";
import BundleCartItem from "./BundleCartItem";
import { bundleAwareCartItems } from "../../utils/bundleAwareCartItems";
import PromoCodes from "../promo_codes/PromoCodes";


const CartOverlay = ({ isMobile }) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const isOpen = useSelector((state) => state.cart.cartOpen);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const transformedCartItems = bundleAwareCartItems(cartItems);
  const submittedPromoCodes = useSelector(state => state.cart.submittedPromoCodes);
  const subtotal = calculateSubtotal(cartItems);
  const { discountedTotal } = calculateTotal(subtotal, submittedPromoCodes);

  const location = useLocation();
  const isCheckoutPage = location.pathname.startsWith('/checkout');


  return (
    <ModalWrapper isOpen={isOpen} onClose={() => dispatch(closeCart())} className="cart-modal">

      {submittedPromoCodes?.length > 0 && (
        <div className="cart-promo-codes flex flex-col gap-2">
          <p className="text-md font-bold mb-0">Active Promo Codes</p>
          <PromoCodes promoCodes={submittedPromoCodes} />
        </div>
      )}

      {cartItems.length > 0 ? (
        <div className="cart-items-cont flex flex-col gap-5">
          {isMobile && (
            <div className="cart-slider-cont relative">
              <Swiper
                modules={[Pagination]}
                pagination={{ el: ".slider-cart-pagination", clickable: true }}
                spaceBetween={10}
                slidesOffsetAfter={0}
                slidesPerGroup={1}
                slidesPerView={1}
              >
                {transformedCartItems.map((item) => (
                  <SwiperSlide key={item.cartId}>
                    {item.type === 'bundle' ? (
                      <BundleCartItem key={item.cartId} bundle={item} isMobile={isMobile} />
                    ) : (
                      <CartItem key={item.cartId} item={item} />
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className={`${transformedCartItems.length !== 1 ? '' : 'hidden'} slider-cart-pagination slider-pagination mt-4 gap-2`} />
            </div>
          )}

          {isMobile && transformedCartItems.length !== 1 && (
            <div className="cart-minified-list flex flex-col gap-3 md:gap-4 justify-end rounded-15 px-5">
              {transformedCartItems.map((item) => (
                <div key={item.cartId} className="flex justify-between gap-5 pb-1 border-b border-black">
                  {item.type === 'bundle' ? (
                    <>
                      <p className="text-sm font-medium mb-0">
                        {item.name} ({item.products.length} items)
                        <span className="color-primary ml-1">x{item.quantity}</span>
                      </p>
                      <div className="text-right">
                        <p className="text-sm font-medium mb-0">
                          {formatDisplayPrice(item.products.reduce((total, product) =>
                            total + (product.price * product.quantity), 0
                          ))}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium mb-0">
                        {item.name} | <span className="capitalize">{item.tag}</span>
                        {item.subscription && (
                          <span className="mb-2 ml-1">
                            <span className="text-[10px] px-2 py-1 bg-secondary text-white rounded-20">SUBSCRIPTION</span>
                          </span>
                        )}
                        <span className="color-primary ml-1">x{item.quantity}</span>
                      </p>
                      <ProductPrice quantity={item.quantity} price={item.price} isSubscribed={item.subscription} className="smaller-prices" />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {isMobile && <div className="pb-[90px]" />}

          {!isMobile && (
            <div className="cart-items flex flex-col gap-5">
              {transformedCartItems.map((item) => (
                item.type === 'bundle' ? (
                  <BundleCartItem key={item.cartId} bundle={item} isMobile={isMobile} />
                ) : (
                  <CartItem key={item.cartId} item={item} />
                )
              ))}
            </div>
          )}

          <div className={`btn-container ${isMobile ? 'btn-container-mobile' : ''} flex items-end justify-between flex-wrap gap-3`}>
            <Button
              type="button"
              onClick={() => {
                navigate("/checkout");
                dispatch(closeCart());
              }}
              className="fit-width"
              variant="filled"
            >
              {isCheckoutPage ? 'Return to Checkout' : 'Go to Checkout'}
            </Button>

            <div className="subtotal-cont flex justify-between items-end form-title body-heading h4 gap-2">
              <span className="text-sm">Total:</span>
              <span className="color-primary">{formatDisplayPrice(discountedTotal)}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="empty-cart-cont flex flex-col gap-5 items-center p-10">
          <p>Your cart is empty.</p>
          <Button
            type="button"
            onClick={() => {
              navigate("/customizer");
              dispatch(closeCart());
              trackCTA('Start Personalizing');
            }}
            className="fit-width"
            variant="filled"
          >
            Start Personalizing
          </Button>
        </div>
      )}
    </ModalWrapper>
  );
};

CartOverlay.propTypes = {
  isMobile: PropTypes.bool
};

export default CartOverlay;